import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { BsSearch } from "react-icons/bs";
import { FaTimes } from "react-icons/fa";
import { HiArrowUpRight } from "react-icons/hi2";

import { cn } from "../../ui";
import { MENU_GROUPS } from "../sidebar/menu";
import type { MenuItem, Role } from "../sidebar/types";

type Props = {
  open: boolean;
  onClose: () => void;
  role?: Role;
};

type Entry = {
  group: string;
  groupIcon: React.ReactNode;
  item: MenuItem;
};

const isMac = () =>
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform);

const flatten = (role: Role | undefined): Entry[] => {
  if (!role) return [];
  const entries: Entry[] = [];
  for (const group of MENU_GROUPS) {
    if (!group.roles.includes(role)) continue;
    for (const item of group.menu) {
      if (item.roles && !item.roles.includes(role)) continue;
      entries.push({ group: group.title, groupIcon: group.icon, item });
    }
  }
  return entries;
};

const matches = (e: Entry, q: string) => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    e.item.title.toLowerCase().includes(needle) ||
    e.group.toLowerCase().includes(needle) ||
    e.item.ref.toLowerCase().includes(needle)
  );
};

const CommandPalette: React.FC<Props> = ({ open, onClose, role }) => {
  const [mounted, setMounted] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => setMounted(true), []);

  const allEntries = useMemo(() => flatten(role), [role]);
  const results = useMemo(
    () => allEntries.filter((e) => matches(e, query.trim())),
    [allEntries, query]
  );

  const grouped = useMemo(() => {
    const byGroup = new Map<string, Entry[]>();
    for (const e of results) {
      if (!byGroup.has(e.group)) byGroup.set(e.group, []);
      byGroup.get(e.group)!.push(e);
    }
    return Array.from(byGroup, ([groupTitle, items]) => ({
      groupTitle,
      items,
    }));
  }, [results]);

  // Reset active index & query when opened.
  useLayoutEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      // focus the input after the portal has rendered
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Clamp active index to current results.
  useEffect(() => {
    if (active >= results.length) setActive(Math.max(0, results.length - 1));
  }, [results, active]);

  // Scroll active option into view.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>(`[data-idx="${active}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  // Keyboard: close / navigate.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min(results.length - 1, i + 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const pick = results[active];
        if (pick) {
          navigate(pick.item.ref);
          onClose();
        }
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, active, navigate, onClose]);

  // Body scroll lock.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!mounted || !open) return null;

  // Flat running index across groups so keyboard nav works predictably.
  let flatIdx = -1;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999999] flex items-start justify-center p-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
    >
      {/* Backdrop — its own click closes the palette */}
      <button
        type="button"
        aria-label="Close command palette"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-gray-900/40 backdrop-blur-[2px]"
      />

      <div
        className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_30px_80px_-30px_rgba(16,24,40,0.35)] outline-none"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 border-b border-gray-100 px-4">
          <BsSearch className="h-4 w-4 shrink-0 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            placeholder="Jump to a section, admin or module…"
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            className="h-12 flex-1 bg-transparent text-[14px] text-gray-900 placeholder:text-gray-400 focus:outline-none"
          />
          <kbd className="hidden rounded-md border border-gray-200 bg-gray-50 px-1.5 py-[1px] text-[10px] font-semibold text-gray-500 sm:inline-block">
            ESC
          </kbd>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25"
          >
            <FaTimes className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {grouped.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <p className="text-[13px] font-medium text-gray-900">
                No matches for{" "}
                <span className="text-brand">{`"${query}"`}</span>
              </p>
              <p className="mt-1 text-[11.5px] text-gray-500">
                Try another keyword or check your spelling.
              </p>
            </div>
          ) : (
            <ul ref={listRef} className="py-2" role="listbox">
              {grouped.map(({ groupTitle, items }) => (
                <li key={groupTitle} className="mb-1 last:mb-0">
                  <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">
                    {groupTitle}
                  </div>
                  <ul>
                    {items.map((e) => {
                      flatIdx += 1;
                      const idx = flatIdx;
                      const isActive = idx === active;
                      return (
                        <li key={`${e.group}:${e.item.ref}`}>
                          <button
                            type="button"
                            data-idx={idx}
                            onMouseEnter={() => setActive(idx)}
                            onClick={() => {
                              navigate(e.item.ref);
                              onClose();
                            }}
                            className={cn(
                              "flex w-full items-center gap-3 px-4 py-2 text-left transition-colors",
                              isActive ? "bg-brand/5" : "hover:bg-gray-50"
                            )}
                          >
                            <span
                              className={cn(
                                "flex h-7 w-7 shrink-0 items-center justify-center rounded-md ring-1 ring-inset transition-colors",
                                isActive
                                  ? "bg-brand/10 text-brand ring-brand/20"
                                  : "bg-gray-50 text-gray-500 ring-gray-200"
                              )}
                            >
                              {e.groupIcon}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p
                                className={cn(
                                  "truncate text-[13px] font-medium",
                                  isActive ? "text-gray-900" : "text-gray-800"
                                )}
                              >
                                {e.item.title}
                              </p>
                              <p className="truncate text-[11px] text-gray-500">
                                {e.item.ref}
                              </p>
                            </div>
                            {isActive ? (
                              <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-brand">
                                Open
                                <HiArrowUpRight className="h-3 w-3" />
                              </span>
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-gray-100 bg-gray-50/60 px-4 py-2 text-[10.5px] text-gray-500">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Kbd>↑</Kbd>
              <Kbd>↓</Kbd>
              navigate
            </span>
            <span className="inline-flex items-center gap-1">
              <Kbd>↵</Kbd>
              open
            </span>
          </div>
          <span className="inline-flex items-center gap-1">
            <Kbd>{isMac() ? "⌘" : "Ctrl"}</Kbd>
            <Kbd>K</Kbd>
            to open
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="rounded border border-gray-200 bg-white px-1 py-[1px] font-semibold text-gray-600 shadow-sm">
    {children}
  </kbd>
);

export default CommandPalette;
